import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { debounce } from 'lodash';
import Client from '@/api/Client';
import { useAuthStore } from '@/store/authStore';
import { checkError } from '@/api/Error';
import { firstLetter as getFirstLetter } from '@/utils/filters';

interface SearchUser {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  avatar_url?: string;
}

const UserGlobalSearch: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);

  const fetchUsers = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const client = new Client();
        const res = await client.request(token, 'get', 'users', null, { search: query, per: 10 });
        setOptions(res.data.users || []);
      } catch (error: any) {
        checkError(error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const debouncedFetch = useMemo(() => debounce(fetchUsers, 400), [fetchUsers]);

  useEffect(() => {
    debouncedFetch(inputValue);
    return () => debouncedFetch.cancel();
  }, [inputValue, debouncedFetch]);

  const getInitials = (firstName: string, lastName: string) =>
    `${getFirstLetter(firstName)}${getFirstLetter(lastName)}`;

  return (
    <Autocomplete
      size="small"
      sx={{
        width: '100%',
        maxWidth: 460,
        '& .MuiOutlinedInput-root': {
          bgcolor: '#F6F6F3',
          borderRadius: '9px',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E7E5E0' },
          '&.Mui-focused': { bgcolor: '#FFFFFF' },
        },
      }}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 2 ? 'Saisir au moins 2 caractères' : 'Aucun utilisateur trouvé'}
      onChange={(_, user) => {
        if (user) {
          navigate(`/utilisateurs/${user.id}`);
          setInputValue('');
          setOptions([]);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Rechercher un utilisateur — nom, email…"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ color: 'text.secondary', ml: 0.5, mr: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, user) => (
        <Box component="li" {...props} key={user.id}>
          <Avatar src={user.avatar_url} sx={{ width: 32, height: 32, mr: 1.5, fontSize: 14 }}>
            {getInitials(user.first_name, user.last_name)}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user.first_name} {user.last_name}
            </Typography>
            {user.email && (
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
};

export default UserGlobalSearch;
